import { Alert, Input, Layouts, Loader, TextArea, UploadImages } from 'components'
import s from './styles.module.scss'
import DashBoardNav from '../../dashboardNav'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from 'api'
import { head, imageCompressor } from 'helpers'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

export default function Main() {
  const [loadingStatus, setLoadingStatus] = useState('Loading...')
  const [loading, setLoading] = useState(false)
  const [oldBlogs, setOldBlogs] = useState([])
  const [blogs, setBlogs] = useState([])
  const [oldBlogImages, setOldBlogImages] = useState([])
  const [oldBlogImagesUrls, setOldBlogImagesUrls] = useState([])
  const [oldImagesRemoved, setOldImagesRemoved] = useState([])
  const [newBlogImages, setNewBlogImages] = useState([])
  const [newBlogImagesUrls, setNewBlogImagesUrls] = useState([])

  const navigate = useNavigate()
  const processing = useRef(false)

  const updateOldBlogs = (field, value, index) => {
    const updated = [...oldBlogs]
    updated[index][field] = value
    setOldBlogs(updated)
  }

  const updateNewBlogs = (field, value, index) => {
    const updated = [...blogs]
    updated[index][field] = value
    setBlogs(updated)
  }

  const handleOldBlogImage = async (e, index) => {
    const file = Array.from(e.target.files)[0]
    if (!file) return
    const [compressed] = await imageCompressor([file], 'logo')
    if (!compressed) return

    const imageUrl = URL.createObjectURL(compressed)
    const updatedImages = [...oldBlogImages]
    updatedImages[index] = compressed
    setOldBlogImages(updatedImages)

    const updatedUrls = [...oldBlogImagesUrls]
    updatedUrls[index] = imageUrl
    setOldBlogImagesUrls(updatedUrls)
  }

  const handleNewBlogImage = async (e, index) => {
    const file = Array.from(e.target.files)[0]
    if (!file) return
    const [compressed] = await imageCompressor([file], 'logo')
    if (!compressed) return

    const imageUrl = URL.createObjectURL(compressed)
    const updatedImages = [...newBlogImages]
    updatedImages[index] = compressed
    setNewBlogImages(updatedImages)

    const updatedUrls = [...newBlogImagesUrls]
    updatedUrls[index] = imageUrl
    setNewBlogImagesUrls(updatedUrls)
  }

  const removeOldBlog = index => {
    const removedImage = oldBlogs[index].image
    if (removedImage) setOldImagesRemoved(prev => [...prev, removedImage])
    setOldBlogs(prev => prev.filter((_, i) => i !== index))
    setOldBlogImages(prev => prev.filter((_, i) => i !== index))
    setOldBlogImagesUrls(prev => prev.filter((_, i) => i !== index))
  }

  const removeNewBlog = index => {
    setBlogs(prev => prev.filter((_, i) => i !== index))
    setNewBlogImages(prev => prev.filter((_, i) => i !== index))
    setNewBlogImagesUrls(prev => prev.filter((_, i) => i !== index))
  }

  const fetchBlogs = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    const res = await api.admin.blog.fetch({})
    if (res.code === 200) {
      setOldBlogs(res.payload.blogs)
      setOldBlogImages(Array(res.payload.blogs.length).fill(null))
      setOldBlogImagesUrls(res.payload.blogs.map(item => (item.image ? IMAGE_HOST + item.image : null)))
    } else {
      Alert.error(res.message)
    }

    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    fetchBlogs()
  }, [fetchBlogs])

  const saveHandler = async () => {
    if (processing.current) return

    const allBlogs = [...oldBlogs, ...blogs]
    const isEmpty = allBlogs.some(x =>
      !x.title?.trim() || !x.description?.trim() || !x.bloggerName?.trim()
    )
    if (isEmpty) return Alert.warn('All fields are required in each blog item.')

    processing.current = true
    Alert.success('Saving Blogs...')
    setLoading(true)

    const updatedOld = [...oldBlogs]
    const updatedNew = [...blogs]

    oldBlogImages.forEach((img, i) => {
      if (img) updatedOld[i].image = img.name
    })
    newBlogImages.forEach((img, i) => {
      if (img) updatedNew[i].image = img.name
    })

    const data = new FormData()
    data.append('oldBlogs', JSON.stringify(updatedOld))
    data.append('blogs', JSON.stringify(updatedNew))
    data.append('oldImagesRemoved', JSON.stringify(oldImagesRemoved))

    ;[...oldBlogImages, ...newBlogImages].forEach(img => {
      if (img) data.append('IMAGES', img)
    })

    const res = await api.admin.blog.update(data)

    if (res.code === 201) {
      Alert.success(res.message)
      navigate(-1)
    } else {
      Alert.error(res.message)
    }

    setLoading(false)
    processing.current = false
  }

  useEffect(() => {
    head({ title: 'Blog | Admin' })
  }, [])

  return (
    <div className={s.main}>
      <div className={s.testimoniesOuter}>
        <Layouts.Classic title='Blogs'>
          <div className={s.headerBottom}>
            <div className={s.addTestimony} onClick={saveHandler}>
              <span className='material-icons-outlined'>save</span> Save
            </div>
          </div>
        </Layouts.Classic>

        {!loading && (
          <div className={s.content + ' innerScrollX'}>
            <DashBoardNav />
            <div className={s.testimonies}>
              <div className={s.label}>Blogs</div>

              {oldBlogs.map((item, i) => (
                <BlogItem
                  key={i}
                  index={i}
                  item={item}
                  imageUrl={oldBlogImagesUrls[i]}
                  onChange={updateOldBlogs}
                  onRemove={removeOldBlog}
                  onImageChange={handleOldBlogImage}
                />
              ))}

              {blogs.map((item, i) => (
                <BlogItem
                  key={i}
                  index={i + oldBlogs.length}
                  item={item}
                  imageUrl={newBlogImagesUrls[i]}
                  onChange={(field, value) => updateNewBlogs(field, value, i)}
                  onRemove={() => removeNewBlog(i)}
                  onImageChange={e => handleNewBlogImage(e, i)}
                />
              ))}
            </div>

            <div className={s.addMore}>
              <div
                onClick={() => {
                  setBlogs(prev => [...prev, { title: '', description: '', bloggerName: '', image: '' }])
                  setNewBlogImages(prev => [...prev, null])
                  setNewBlogImagesUrls(prev => [...prev, null])
                }}
              >
                Add Blog <span className='material-icons-outlined'>add_circle_outline</span>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className={s.loader}>
            <Loader message={loadingStatus} color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        )}
      </div>
    </div>
  )
}

const BlogItem = ({ index, item, imageUrl, onChange, onRemove, onImageChange }) => (
  <div className={s.testimony}>
    <div className={s.title}>
      <span>Blog {index + 1}</span>
      <span onClick={() => onRemove(index)}>
        Remove<span className='material-icons-outlined'>cancel</span>
      </span>
    </div>

    <div className={s.imageUpload}>
      <UploadImages.Single
        userLogoUrl={imageUrl || ''}
        handleUserLogoChange={e => onImageChange(e, index)}
        id={`imageUpload${index}`}
      />
    </div>

    <TextArea.Classic
      label='Description'
      iconLeft='description'
      placeholder='Enter blog description...'
      value={item.description}
      onChange={e => onChange('description', e.target.value, index)}
    />

    <Input.Classic
      type='text'
      label='Blogger Name'
      iconLeft='person'
      placeholder='Enter blogger name'
      value={item.bloggerName}
      onChange={e => onChange('bloggerName', e.target.value, index)}
    />

    <Input.Classic
      type='text'
      label='Title'
      iconLeft='title'
      placeholder='Enter blog title'
      value={item.title}
      onChange={e => onChange('title', e.target.value, index)}
    />
  </div>
)
